import type { FirebaseOptions } from 'firebase/app';
import { HOME_REGION, type Region } from '@crewdle/auth-bridge';

/**
 * Data residency (Milestone B): one Firebase project per region. `ca` is the
 * app's default (GLOBAL) project — `environment.firebase` — and also holds
 * users/{uid}, orgDirectory, catalog and white-label docs. Every org's data
 * lives in its region's project.
 */
export interface RegionEndpoints {
  /**
   * Firebase web config of the region's project. Omitted for `ca` (the
   * default app). `null` = the region's project isn't provisioned yet: orgs
   * there get the "region unavailable" state — never a silent fallback to ca.
   */
  firebase?: FirebaseOptions | null;
  /** Cloud Functions region of the project. */
  functionsRegion: string;
  /** Cloudlet / AI API gateway of the region (`api.<region>.crewdle.ai`). */
  apiUrl: string;
}

/** Per-region endpoints. A region set to `null` has no stack in this environment. */
export type RegionsConfig = Record<Region, RegionEndpoints | null>;

export { HOME_REGION };

/** Firebase configs of the REMOTE regions, in the shape `AuthBridge.connectRegion` expects. */
export function regionalFirebaseConfigs(regions: RegionsConfig): Partial<Record<Region, FirebaseOptions>> {
  const out: Partial<Record<Region, FirebaseOptions>> = {};
  for (const region of Object.keys(regions) as Region[]) {
    const config = regions[region]?.firebase;
    if (region !== HOME_REGION && config) out[region] = config;
  }
  return out;
}

/** Raised when org data is touched while the active org's region has no live stack. */
export class RegionUnavailableError extends Error {
  constructor(readonly orgId: string | null, detail?: string) {
    super(`region-unavailable${orgId ? ` (org ${orgId})` : ''}${detail ? `: ${detail}` : ''}`);
    this.name = 'RegionUnavailableError';
  }
}

/** `https://<functionsRegion>-<projectId>.cloudfunctions.net/<name>` (HTTP functions). */
export function cloudFunctionUrl(projectId: string, functionsRegion: string, name: string): string {
  return `https://${functionsRegion}-${projectId}.cloudfunctions.net/${name}`;
}
